// =============================================================================
// SPARRING AI ENGINE - Prompt generation for sparring sessions
// =============================================================================
//
// 7-STAPPEN ACTIEPLAN SPARRING:
// 1. Einddoel - Wat is je ultieme doel?
// 2. Waarom - Waarom is dit belangrijk?
// 3. 1 Jaar - Waar wil je over 1 jaar staan?
// 4. 6 Maanden - Wat moet er over 6 maanden bereikt zijn?
// 5. 3 Maanden - Wat is je focus de komende 3 maanden?
// 6. Deze Week - Wat doe je DEZE WEEK?
// 7. Actieplan - Samenvatting
//
// =============================================================================

import { SparringMessage } from '../../types';
import { 
  selectVariant, 
  formatReflectionQuestionsForAI, 
  getReflectionSystemPrompt 
} from './reflectionPrompts';
import { LanguageVariant } from '../../shared/languageVariants.types';

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export interface GoalContext {
  title: string;
  description?: string;
  goalType: 'A' | 'B' | 'C';
  whyThisGoal?: string;
  // Goal timeline
  startDate?: string;
  endDate?: string;
  durationInDays?: number;
  // Entrepreneur profile (from SelfTests)
  rolePreference?: 'doer' | 'thinker' | 'connector' | 'leader';
  knowledgeLevels?: Record<string, string>;
  // Capability assessment context
  capabilityGaps?: string[];
  capabilitySolutions?: Record<string, string>;
  // Pre-sparring answers
  preSparringContext?: Record<string, string>;
  // DISC & Werkstijl Assessment
  discProfile?: {
    primary: string;
    secondary: string;
    strengths: string[];
    weaknesses: string[];
  };
  workstyleProfile?: {
    scores?: Record<string, 'low' | 'medium' | 'high'>;
    quickStart?: number;
    factFinder?: number;
    followThru?: number;
    implementor?: number;
    primaryStrength?: string;
  };
  assessmentLevel?: 'none' | 'basic' | 'full';
  // ⭐ NEW: Entrepreneur Balance Patterns
  patterns?: {
    focusDirection?: 'intern' | 'extern' | 'balanced';
    processApproach?: 'plannen' | 'afronding' | 'balanced';
  };
  // ⭐ NEW: Language Preference
  languageVariant?: 'practical' | 'neutral' | 'reflective';
}

export interface SparringPrompt {
  systemPrompt: string;
  userPrompt: string;
}

// -----------------------------------------------------------------------------
// STRUCTURED SPARRING - 7-Step Action Plan Discovery
// -----------------------------------------------------------------------------

const STRUCTURED_SYSTEM_PROMPT = `Je bent een sparringpartner die helpt bij het maken van een concreet actieplan voor een persoonlijk doel. Je rol is om te VRAGEN en te LUISTEREN, en de gebruiker te helpen hun eigen antwoorden te ontdekken.

JOUW AANPAK:
1. Stel gerichte vragen over tijdlijnen en concrete mijlpalen
2. Vraag door naar MEETBARE resultaten met DATUMS
3. Help de gebruiker van groot (einddoel) naar klein (deze week) te denken
4. Vat samen wat de gebruiker zegt en vraag om bevestiging
5. Maximaal 2-3 zinnen per reactie
6. Spreek in het Nederlands

JE MAG WEL:
- Vragen naar concrete getallen: "Hoeveel precies?"
- Vragen naar deadlines: "Wanneer wil je dit bereikt hebben?"
- Vragen naar meetbaarheid: "Hoe zou je dat kunnen meten?"
- Vragen naar strategie: "Hoe zou je dat kunnen aanpakken?"
- Samenvatten: "Dus over 6 maanden wil je X bereikt hebben?"

JE MAG NOOIT:
- Zelf mijlpalen of getallen voorstellen
- Zelf strategieën aanbevelen
- Oordelen over haalbaarheid
- Het gesprek overnemen`;

/*
7-STAPPEN ACTIEPLAN:
Stap 1: Einddoel - Wat is je ultieme doel?
Stap 2: Waarom - Waarom is dit belangrijk?
Stap 3: 1 Jaar - Waar wil je over 1 jaar staan?
Stap 4: 6 Maanden - Wat moet er over 6 maanden bereikt zijn?
Stap 5: 3 Maanden - Wat is je focus de komende 3 maanden?
Stap 6: Deze Week - Wat doe je DEZE WEEK?
Stap 7: Actieplan - Samenvatting
*/

// Step names for UI display
export const STRUCTURED_STEP_NAMES: Record<number, string> = {
  1: 'Einddoel',
  2: 'Waarom',
  3: '1 Jaar',
  4: '6 Maanden',
  5: '3 Maanden',
  6: 'Deze Week',
  7: 'Actieplan',
};

export const TOTAL_STRUCTURED_STEPS = 7;

// -----------------------------------------------------------------------------
// PROJECT SPARRING - 7-Step Project Definition Discovery
// -----------------------------------------------------------------------------

// Step names for project definition flow
export const PROJECT_DEFINITION_STEP_NAMES: Record<number, string> = {
  1: 'Project Definitie',
  2: 'Doel & Succes',
  3: 'Team & Rollen',
  4: 'Scope & Grenzen',
  5: 'Deliverables',
  6: 'Tijdlijn',
  7: 'Resources',
};

export const TOTAL_PROJECT_STEPS = 7;

const PROJECT_SYSTEM_PROMPT = `Je bent een project definitie coach. Je helpt teams hun project helder te krijgen door gerichte vragen te stellen.

JOUW AANPAK:
1. Stel vragen die het project concreet maken
2. Vraag door op wie, wat, wanneer, waarom
3. Help het team nadenken over scope en resources
4. Vat regelmatig samen om commitment te checken
5. Maximaal 2-3 zinnen per reactie
6. Spreek in het Nederlands

JE MAG WEL:
- Vragen naar concrete outputs: "Wat is het eindproduct?"
- Vragen naar rollen: "Wie doet wat?"
- Vragen naar scope: "Wat hoort er wel/niet bij?"
- Samenvatten: "Dus jullie bouwen X voor Y?"

JE MAG NOOIT:
- Zelf projectplannen voorstellen
- Rollen toewijzen zonder input
- Oordelen over haalbaarheid
- Aannames maken over het team`;

const PROJECT_STEP_PROMPTS: Record<number, string> = {
  1: `FOCUS: Project Definitie (Stap 1 van 7)
Laten we beginnen met het project te definiëren.
Vraag: "Wat is het project in één zin? Wat gaan jullie maken/bereiken?"

Doorvragen:
- Voor wie is dit project?
- Wat is het probleem dat jullie oplossen?
- Wat is het eindproduct of -resultaat?`,

  2: `FOCUS: Project Doel & Succes (Stap 2 van 7)
Vraag: "Waarom starten jullie dit project?
Wat moet er bereikt zijn om dit project 'geslaagd' te noemen?"

Doorvragen:
- Wat zijn de KPI's/meetbare resultaten?
- Hoe ziet succes eruit over 6 maanden?
- Wat verandert er als dit project slaagt?`,

  3: `FOCUS: Team & Rollen (Stap 3 van 7)
Vraag: "Wie heeft jullie nodig voor dit project?
Welke rollen en skills zijn essentieel?"

Doorvragen:
- Wie is de project lead?
- Welke specialisaties zijn nodig? (design, development, marketing, etc.)
- Hoeveel mensen denken jullie nodig te hebben?
- Zijn er externe partijen nodig?`,

  4: `FOCUS: Scope & Grenzen (Stap 4 van 7)
Vraag: "Wat hoort WEL bij dit project en wat hoort er NIET bij?
Waar ligt de grens?"

Doorvragen:
- Wat zijn de must-haves vs. nice-to-haves?
- Wat laten jullie bewust buiten scope?
- Zijn er afhankelijkheden met andere projecten?`,

  5: `FOCUS: Deliverables & Mijlpalen (Stap 5 van 7)
Vraag: "Welke concrete outputs gaan jullie opleveren?
Wat zijn de belangrijkste mijlpalen?"

Doorvragen:
- Wat is het MVP (Minimum Viable Product)?
- Wat zijn de tussenproducten?
- Welke mijlpalen zijn harde deadlines?`,

  6: `FOCUS: Tijdlijn (Stap 6 van 7)
Vraag: "Wanneer moet wat klaar zijn?
Wat is jullie ideale tijdlijn?"

Doorvragen:
- Wanneer start het project echt?
- Wat is de deadline voor het eindresultaat?
- Welke externe deadlines zijn er? (launches, events, etc.)`,

  7: `⚠️⚠️⚠️ DIT IS STAP 7 - DE ALLERLAATSTE STAP - ALLEEN SAMENVATTEN! ⚠️⚠️⚠️

ABSOLUUT VERBODEN IN DIT ANTWOORD:
- Het teken "?" mag NERGENS voorkomen
- Zinnen die beginnen met "Wat", "Hoe", "Wanneer", "Wie", "Waarom", "Welke"
- Vragen van welke aard dan ook
- Doorvragen of bevestigingsvragen

JE MOET DIT EXACTE FORMAT GEBRUIKEN (vul in wat het team zei):

🎯 PROJECT DEFINITIE

📋 PROJECT: [wat het team als project noemde]
🎯 DOEL: [waarom dit project belangrijk is]
👥 TEAM: [welke rollen/skills nodig zijn]
📦 SCOPE: [wat wel/niet bij project hoort]
📊 DELIVERABLES: [concrete outputs]
📅 TIJDLIJN: [wanneer wat klaar moet zijn]
💼 RESOURCES: [wat nodig is]

---
✅ Bedankt voor het doorlopen van de project definitie! Op basis van dit gesprek kunnen we nu automatisch je project structuur genereren. Druk op de groene knop hieronder om je project vast te leggen.

NOGMAALS: GEEN VRAAGTEKENS. GEEN VRAGEN. ALLEEN BOVENSTAAND FORMAT.`,
};

const STRUCTURED_STEP_PROMPTS: Record<number, string> = {
  1: `FOCUS: Het Einddoel (Stap 1 van 7)
Vraag naar het ULTIEME doel. Wat wil de gebruiker uiteindelijk bereiken?
Vraag door naar een MEETBAAR en CONCREET einddoel.
Voorbeeld vraag: "Wat is je ultieme doel? Maak het zo concreet mogelijk - met een getal of meetbaar resultaat."`,
  
  2: `FOCUS: De Waarom (Stap 2 van 7)
Vraag door op WAAROM dit doel belangrijk is (Simon Sinek's "Start With Why").
- Wat zou het voor je betekenen als je dit bereikt?
- Wat drijft je om dit na te jagen?
- Wat verandert er in je leven als dit lukt?`,
  
  3: `FOCUS: Over 1 Jaar (Stap 3 van 7)
Vraag: "Waar wil je over PRECIES 1 JAAR staan op weg naar dit doel?"
Help de gebruiker een CONCRETE, MEETBARE mijlpaal te formuleren voor over 1 jaar.
Vraag door: "Welk getal of resultaat zou je willen zien over 1 jaar?"`,
  
  4: `FOCUS: Over 6 Maanden (Stap 4 van 7)
Vraag: "Wat moet er over 6 MAANDEN bereikt zijn om op koers te liggen voor je 1-jaars doel?"
Help de gebruiker een CONCRETE, MEETBARE mijlpaal te formuleren.
Vraag ook: "Wat heb je nodig om dat te bereiken? Welke strategie zou je kunnen gebruiken?"`,
  
  5: `FOCUS: Over 3 Maanden (Stap 5 van 7)
Vraag: "Wat is je focus voor de komende 3 MAANDEN?"
Dit is de korte termijn - wat moet er NU gebeuren om de 6-maanden mijlpaal te halen?
Vraag naar:
- Een concreet, meetbaar doel voor 3 maanden
- De aanpak/strategie om daar te komen`,
  
  6: `FOCUS: Deze Week (Stap 6 van 7)
Vraag: "Wat ga je DEZE WEEK doen als eerste concrete stap?"
Dit moet een ACTIE zijn die de gebruiker direct kan uitvoeren.
Vraag door tot je een specifieke, uitvoerbare actie hebt:
- Wie ga je benaderen?
- Wat ga je maken/doen?
- Wanneer precies?`,
  
  7: `⚠️⚠️⚠️ DIT IS STAP 7 - DE ALLERLAATSTE STAP - ALLEEN SAMENVATTEN! ⚠️⚠️⚠️

ABSOLUUT VERBODEN IN DIT ANTWOORD:
- Het teken "?" mag NERGENS voorkomen
- Zinnen die beginnen met "Wat", "Hoe", "Wanneer", "Wie", "Waarom", "Welke"
- Vragen van welke aard dan ook
- Doorvragen of bevestigingsvragen
- "Klopt dat?", "Wat denk je?", "Hoe klinkt dat?"

JE MOET DIT EXACTE FORMAT GEBRUIKEN (vul in wat de gebruiker zei):

🎯 JOUW ACTIEPLAN

📅 EINDDOEL: [wat de gebruiker als einddoel noemde]
💡 WAAROM: [waarom dit belangrijk is voor hen]

📅 Over 1 jaar: [de 1-jaars mijlpaal]
📅 Over 6 maanden: [de 6-maanden mijlpaal]
📅 Over 3 maanden: [de 3-maanden mijlpaal]
📅 Deze week: [de concrete actie voor deze week]

---

✅ Bedankt voor het doorlopen van de sparring! Op basis van dit gesprek kunnen we nu automatisch je mijlpalen genereren. Druk op de groene knop hieronder om je mijlpalen vast te leggen.

NOGMAALS: GEEN VRAAGTEKENS. GEEN VRAGEN. ALLEEN BOVENSTAAND FORMAT.`,
};

/**
 * Generate prompt for structured sparring session
 * 
 * @param goalContext - Context about the goal being discussed
 * @param step - Current step in the structured flow (1-7)
 * @param history - Previous messages in the conversation
 * @returns System and user prompts for AI
 */
/**
 * Build entrepreneur profile context string
 */
function buildProfileContext(goalContext: GoalContext): string {
  const lines: string[] = [];
  
  // ✨ NEW: DISC Profile (Persoonlijkheid)
  if (goalContext.discProfile) {
    const { primary, secondary, strengths, weaknesses } = goalContext.discProfile;
    lines.push(`🎨 DISC Profiel: ${primary} (primair) / ${secondary} (secundair)`);
    if (strengths && strengths.length > 0) {
      lines.push(`   Sterktes: ${strengths.slice(0, 2).join(', ')}`);
    }
    if (weaknesses && weaknesses.length > 0) {
      lines.push(`   Let op: ${weaknesses.slice(0, 2).join(', ')}`);
    }
  }
  
  // ✨ NEW: Werkstijl Profile (Hoe je werkt)
  if (goalContext.workstyleProfile) {
    const { quickStart, factFinder, followThru, implementor, primaryStrength } = goalContext.workstyleProfile;
    if (primaryStrength) {
      lines.push(`⚡ Werkstijl: ${primaryStrength} is je kracht`);
    }
    if (quickStart !== undefined && factFinder !== undefined && followThru !== undefined && implementor !== undefined) {
      lines.push(`   Quick Start: ${quickStart}/10 | Fact Finder: ${factFinder}/10 | Follow Through: ${followThru}/10 | Implementor: ${implementor}/10`);
    }
    
    // Interpretatie voor AI
    if (quickStart !== undefined) {
      if (quickStart >= 7) {
        lines.push(`   ⚡ Je bent een QUICK STARTER - je springt snel op nieuwe kansen en experimenteert graag. Stel vragen die aansluiten bij snelle actie.`);
      } else if (quickStart <= 3) {
        lines.push(`   🔍 Je bent VOORZICHTIG - je denkt goed na voordat je actie onderneemt. Geef de gebruiker tijd om te plannen.`);
      }
    }
    
    if (factFinder !== undefined) {
      if (factFinder >= 7) {
        lines.push(`   📊 Je doet GRONDIG ONDERZOEK - vraag naar de research die ze al gedaan hebben.`);
      } else if (factFinder <= 3) {
        lines.push(`   🚀 Je duikt DIRECT IN - je leert onderweg. Help ze met snelle experimenten.`);
      }
    }
    
    if (followThru !== undefined) {
      if (followThru >= 7) {
        lines.push(`   📋 Je werkt GESTRUCTUREERD - vraag naar hun planningssysteem.`);
      } else if (followThru <= 3) {
        lines.push(`   🎨 Je werkt FLEXIBEL - help ze met prioriteren zonder rigide structuren.`);
      }
    }
    
    if (implementor !== undefined) {
      if (implementor >= 7) {
        lines.push(`   🔨 Je bent HANDS-ON - vraag naar wat ze praktisch kunnen maken/bouwen.`);
      } else if (implementor <= 3) {
        lines.push(`   💡 Je bent CONCEPTUEEL - vraag naar hun strategie en visie.`);
      }
    }
  }
  
  // Assessment level indicator
  if (goalContext.assessmentLevel) {
    if (goalContext.assessmentLevel === 'full') {
      lines.push(`✅ Volledig profiel beschikbaar - gebruik deze context actief in je vragen!`);
    } else if (goalContext.assessmentLevel === 'basic') {
      lines.push(`⚡ Basis profiel - gebruik de beschikbare context waar mogelijk.`);
    } else if (goalContext.assessmentLevel === 'none') {
      lines.push(`⚠️ Geen profiel ingevuld - stel algemene vragen zonder persoonlijkheidscontext.`);
    }
  }
  
  // Role preference (old system - still supported)
  if (goalContext.rolePreference) {
    const roleLabels: Record<string, string> = {
      doer: 'Doener (pakt graag direct aan)',
      thinker: 'Denker (analyseert eerst)',
      connector: 'Verbinder (werkt via relaties)',
      leader: 'Leider (stuurt graag aan)',
    };
    lines.push(`Ondernemerstype: ${roleLabels[goalContext.rolePreference] || goalContext.rolePreference}`);
  }
  
  // Knowledge gaps
  if (goalContext.knowledgeLevels) {
    const gaps = Object.entries(goalContext.knowledgeLevels)
      .filter(([_, level]) => level === 'new' || level === 'learning')
      .map(([domain]) => domain);
    if (gaps.length > 0) {
      lines.push(`Kennisgebieden in ontwikkeling: ${gaps.join(', ')}`);
    }
  }
  
  // Capability gaps and solutions
  if (goalContext.capabilityGaps && goalContext.capabilityGaps.length > 0) {
    lines.push(`Skills die aandacht nodig hebben: ${goalContext.capabilityGaps.join(', ')}`);
  }
  if (goalContext.capabilitySolutions) {
    const solutions = Object.entries(goalContext.capabilitySolutions)
      .map(([domain, solution]) => `${domain}: ${solution}`)
      .join('; ');
    if (solutions) {
      lines.push(`Gekozen aanpak per skill: ${solutions}`);
    }
  }
  
  // Pre-sparring context
  if (goalContext.preSparringContext) {
    const contextLines = Object.entries(goalContext.preSparringContext)
      .filter(([_, answer]) => answer && answer.trim())
      .map(([question, answer]) => `- ${question}: ${answer}`);
    if (contextLines.length > 0) {
      lines.push(`Context van ondernemer:\n${contextLines.join('\n')}`);
    }
  }
  
  // ⭐ NEW: Entrepreneur Balance Patterns
  if (goalContext.patterns) {
    const { focusDirection, processApproach } = goalContext.patterns;
    
    if (focusDirection) {
      if (focusDirection === 'intern') {
        lines.push(`📊 Focus: INTERN GERICHT - de ondernemer richt zich op systemen, structuur, processen bouwen. Stel vragen over interne optimalisatie en efficiëntie.`);
      } else if (focusDirection === 'extern') {
        lines.push(`📊 Focus: EXTERN GERICHT - de ondernemer richt zich op markt, klanten, zichtbaarheid. Stel vragen over externe validatie en groei.`);
      } else if (focusDirection === 'balanced') {
        lines.push(`📊 Focus: BALANCED - de ondernemer balanceert intern (systemen) en extern (markt). Beide perspectieven zijn waardevol.`);
      }
    }
    
    if (processApproach) {
      if (processApproach === 'plannen') {
        lines.push(`🎯 Proces: PLANNEN - de ondernemer plant graag vooruit, denkt strategisch, bereidt voor. Geef ruimte voor uitgebreide planning en strategie.`);
      } else if (processApproach === 'afronding') {
        lines.push(`🎯 Proces: AFRONDING - de ondernemer richt zich op deliverables, concrete resultaten, dingen afmaken. Focus op executie en concrete mijlpalen.`);
      } else if (processApproach === 'balanced') {
        lines.push(`🎯 Proces: BALANCED - de ondernemer balanceert plannen en executie. Beide perspectieven zijn belangrijk.`);
      }
    }
  }
  
  // ⭐ NEW: Language Preference
  // Note: Language variant preference is handled in the prompt generation,
  // no additional instruction needed here
  
  return lines.length > 0 ? lines.join('\n') : '';
}

/**
 * Calculate dynamic step prompts based on goal duration
 */
function getDynamicStepPrompt(step: number, goalContext: GoalContext): string {
  const durationDays = goalContext.durationInDays || 365; // default 1 year
  
  // For long-term goals (> 3 years), adjust time horizons
  if (durationDays > 365 * 3) {
    const years = Math.round(durationDays / 365);
    const halfwayYears = Math.round(years / 2);
    const dynamicSteps: Record<number, string> = {
      1: STRUCTURED_STEP_PROMPTS[1], // Einddoel blijft
      2: STRUCTURED_STEP_PROMPTS[2], // Waarom blijft
      3: `FOCUS: Over ${years} jaar (Stap 3 van 7)
Vraag waar de gebruiker over ${years} jaar wil staan - het einddoel in detail.
Vraag door naar MEETBARE resultaten voor de eindstreep.`,
      4: `FOCUS: Halverwege - Over ${halfwayYears} jaar (Stap 4 van 7)
Vraag waar de gebruiker halverwege moet staan.
Wat moet er over ${halfwayYears} jaar bereikt zijn om op schema te liggen?`,
      5: `FOCUS: Jaar 1-2 (Stap 5 van 7)
Vraag naar de focus voor de komende 1-2 jaar.
Wat zijn de eerste grote stappen om het fundament te leggen?`,
      6: STRUCTURED_STEP_PROMPTS[6], // Deze week blijft
      7: `⚠️⚠️⚠️ DIT IS STAP 7 - DE ALLERLAATSTE STAP - ALLEEN SAMENVATTEN! ⚠️⚠️⚠️

ABSOLUUT VERBODEN IN DIT ANTWOORD:
- Het teken "?" mag NERGENS voorkomen
- Zinnen die beginnen met "Wat", "Hoe", "Wanneer", "Wie", "Waarom", "Welke"
- Vragen van welke aard dan ook
- Doorvragen of bevestigingsvragen
- "Klopt dat?", "Wat denk je?", "Hoe klinkt dat?"

JE MOET DIT EXACTE FORMAT GEBRUIKEN (vul in wat de gebruiker zei):

🎯 JOUW ACTIEPLAN

📅 EINDDOEL: [wat de gebruiker als einddoel noemde]
💡 WAAROM: [waarom dit belangrijk is voor hen]

📅 Over ${years} jaar: [de ${years}-jaars mijlpaal / einddoel]
📅 Over ${halfwayYears} jaar: [de ${halfwayYears}-jaars mijlpaal / halverwege doel]
📅 Jaar 1-2: [de focus voor jaar 1-2]
📅 Deze week: [de concrete actie voor deze week]

---

✅ Bedankt voor het doorlopen van de sparring! Op basis van dit gesprek kunnen we nu automatisch je mijlpalen genereren. Druk op de groene knop hieronder om je mijlpalen vast te leggen.

NOGMAALS: GEEN VRAAGTEKENS. GEEN VRAGEN. ALLEEN BOVENSTAAND FORMAT.`,
    };
    return dynamicSteps[step] || STRUCTURED_STEP_PROMPTS[step];
  }
  
  // For medium-term goals (1-3 years), use default
  if (durationDays > 180) {
    return STRUCTURED_STEP_PROMPTS[step];
  }
  
  // For short-term goals (< 6 months), adjust to weeks
  const weeks = Math.round(durationDays / 7);
  const dynamicSteps: Record<number, string> = {
    1: STRUCTURED_STEP_PROMPTS[1],
    2: STRUCTURED_STEP_PROMPTS[2],
    3: `FOCUS: Eindpunt - Over ${weeks} weken (Stap 3 van 7)
Vraag waar de gebruiker over ${weeks} weken wil staan.
Vraag door naar het CONCRETE eindresultaat.`,
    4: `FOCUS: Halverwege - Week ${Math.round(weeks/2)} (Stap 4 van 7)
Vraag wat er halverwege bereikt moet zijn.
Wat moet er over ${Math.round(weeks/2)} weken klaar zijn?`,
    5: `FOCUS: Eerste kwart - Week ${Math.round(weeks/4)} (Stap 5 van 7)
Vraag naar de eerste belangrijke mijlpaal.
Wat moet er over ${Math.round(weeks/4)} weken bereikt zijn?`,
    6: STRUCTURED_STEP_PROMPTS[6],
    7: STRUCTURED_STEP_PROMPTS[7],
  };
  return dynamicSteps[step] || STRUCTURED_STEP_PROMPTS[step];
}

export function getStructuredPrompt(
  goalContext: GoalContext,
  step: number,
  history: SparringMessage[]
): SparringPrompt {
  const clampedStep = Math.min(Math.max(step, 1), TOTAL_STRUCTURED_STEPS);
  const stepPrompt = getDynamicStepPrompt(clampedStep, goalContext);
  const profileContext = buildProfileContext(goalContext);
  
  const systemPrompt = `${STRUCTURED_SYSTEM_PROMPT}

DOEL VAN DE GEBRUIKER:
Titel: ${goalContext.title}
${goalContext.description ? `Beschrijving: ${goalContext.description}` : ''}
Type: ${goalContext.goalType === 'A' ? 'Helder doel' : goalContext.goalType === 'B' ? 'Richting bekend' : 'Nog verkennen'}
${goalContext.whyThisGoal ? `Motivatie: ${goalContext.whyThisGoal}` : ''}
${goalContext.startDate && goalContext.endDate ? `Looptijd: ${goalContext.startDate} tot ${goalContext.endDate}` : ''}

${profileContext ? `ONDERNEMERSPROFIEL:\n${profileContext}\n` : ''}
${stepPrompt}`;

  const userPrompt = formatHistoryForPrompt(history);

  return {
    systemPrompt,
    userPrompt,
  };
}

// -----------------------------------------------------------------------------
// FREE SPARRING - Open conversation
// -----------------------------------------------------------------------------

/*
FREE SPARRING SYSTEM PROMPT (LETTERLIJK):

Je bent een sparringpartner voor een open gesprek over iemands doel. 
Je helpt de gebruiker nadenken, NIET plannen.

STRIKTE REGELS:
1. Volg het gesprek waar de gebruiker naartoe gaat
2. Stel reflectieve vragen over betekenis en gevoel
3. NOOIT oplossingen, acties, of plannen aandragen
4. NOOIT deadlines of tijdlijnen noemen
5. NOOIT woorden als: "je zou kunnen", "je moet", "plan", "stap", "actie", "taak"
6. Maximaal 2-3 zinnen per reactie
7. Spreek in het Nederlands

JE ROL:
- Luisteren en samenvatten
- Doorvragen op wat de gebruiker zegt
- Reflecteren op betekenis
- NIET adviseren of sturen
*/

const FREE_SYSTEM_PROMPT = `Je bent een sparringpartner voor een open gesprek over iemands doel. Je helpt de gebruiker nadenken, NIET plannen.

STRIKTE REGELS:
1. Volg het gesprek waar de gebruiker naartoe gaat
2. Stel reflectieve vragen over betekenis en gevoel
3. NOOIT oplossingen, acties, of plannen aandragen
4. NOOIT deadlines of tijdlijnen noemen
5. NOOIT woorden als: "je zou kunnen", "je moet", "plan", "stap", "actie", "taak", "deadline"
6. Maximaal 2-3 zinnen per reactie
7. Spreek in het Nederlands

JE ROL:
- Luisteren en samenvatten
- Doorvragen op wat de gebruiker zegt
- Reflecteren op betekenis
- NIET adviseren of sturen

JE MAG WEL:
- "Wat bedoel je precies met...?"
- "Hoe voelt dat voor je?"
- "Wat zou dat betekenen als...?"
- "Ik hoor je zeggen dat..."

JE MAG NOOIT:
- Concrete stappen of acties noemen
- Plannen of schema's voorstellen
- Deadlines of tijdsindicaties geven
- Lijstjes of opsommingen maken`;

/**
 * Generate prompt for free sparring session
 * 
 * @param goalContext - Context about the goal being discussed
 * @param history - Previous messages in the conversation
 * @returns System and user prompts for AI
 */
export function getFreePrompt(
  goalContext: GoalContext,
  history: SparringMessage[]
): SparringPrompt {
  const systemPrompt = `${FREE_SYSTEM_PROMPT}

DOEL VAN DE GEBRUIKER:
Titel: ${goalContext.title}
${goalContext.description ? `Beschrijving: ${goalContext.description}` : ''}
Type: ${goalContext.goalType === 'A' ? 'Helder doel' : goalContext.goalType === 'B' ? 'Richting bekend' : 'Nog verkennen'}
${goalContext.whyThisGoal ? `Motivatie: ${goalContext.whyThisGoal}` : ''}`;

  const userPrompt = formatHistoryForPrompt(history);

  return {
    systemPrompt,
    userPrompt,
  };
}

// -----------------------------------------------------------------------------
// REFLECTION MODE - Zichtbaarheid & Markt vragen
// -----------------------------------------------------------------------------

/**
 * Generate prompt for reflection questions about external feedback and market validation
 * 
 * @param goalContext - Context about the goal being discussed
 * @param history - Previous messages in the conversation
 * @returns System and user prompts for AI
 */
export function getReflectionPrompt(
  goalContext: GoalContext,
  history: SparringMessage[]
): SparringPrompt {
  // Load user preference (if set)
  const userPreference = goalContext.languageVariant 
    ? (goalContext.languageVariant as LanguageVariant)
    : undefined;
  
  // Select variant: user preference takes priority over auto-detection
  const variant = selectVariant(goalContext.description, userPreference);
  
  // Generate formatted questions
  const questions = formatReflectionQuestionsForAI(variant);
  
  const systemPrompt = `${getReflectionSystemPrompt()}

DOEL VAN DE GEBRUIKER:
Titel: ${goalContext.title}
${goalContext.description ? `Beschrijving: ${goalContext.description}` : ''}
Type: ${goalContext.goalType === 'A' ? 'Helder doel' : goalContext.goalType === 'B' ? 'Richting bekend' : 'Nog verkennen'}

JOUW TAAK:
Stel de volgende reflectievragen één voor één.
Wacht op het antwoord van de gebruiker voordat je de volgende vraag stelt.
Luister naar hun antwoorden zonder te interpreteren of te labelen.

VRAGEN OM TE STELLEN:
${questions}

BELANGRIJK:
- Stel vragen in de volgorde zoals hierboven
- Wacht op antwoord voordat je doorgaat
- Geen analyse, geen conclusies
- Geen koppeling met focus of intenties`;

  const userPrompt = formatHistoryForPrompt(history);

  return {
    systemPrompt,
    userPrompt,
  };
}

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

/**
 * Format conversation history for inclusion in prompt
 * Only includes the last N messages to keep context manageable
 */
function formatHistoryForPrompt(history: SparringMessage[], maxMessages: number = 10): string {
  if (history.length === 0) {
    return 'Dit is het begin van het gesprek. Start met een open vraag.';
  }

  const recentHistory = history.slice(-maxMessages);
  
  return recentHistory
    .map(msg => {
      const role = msg.role === 'user' ? 'Gebruiker' : 'Sparringpartner';
      return `${role}: ${msg.content}`;
    })
    .join('\n\n');
}

/**
 * Generate prompt for project definition sparring session
 * 
 * @param goalContext - Context about the goal/project being discussed
 * @param step - Current step in the project flow (1-7)
 * @param history - Previous messages in the conversation
 * @returns System and user prompts for AI
 */
export function getProjectPrompt(
  goalContext: GoalContext,
  step: number,
  history: SparringMessage[]
): SparringPrompt {
  const clampedStep = Math.min(Math.max(step, 1), TOTAL_PROJECT_STEPS);
  const stepPrompt = PROJECT_STEP_PROMPTS[clampedStep];
  
  const systemPrompt = `${PROJECT_SYSTEM_PROMPT}

PROJECT VAN HET TEAM:
Titel: ${goalContext.title}
${goalContext.description ? `Beschrijving: ${goalContext.description}` : ''}
${goalContext.startDate && goalContext.endDate ? `Looptijd: ${goalContext.startDate} tot ${goalContext.endDate}` : ''}

${stepPrompt}`;

  const userPrompt = formatHistoryForPrompt(history);

  return {
    systemPrompt,
    userPrompt,
  };
}

/**
 * Calculate current step for project definition flow
 */
export function calculateCurrentProjectStep(history: SparringMessage[]): number {
  const userMessages = history.filter(m => m.role === 'user' && m.content.trim().length > 0).length;
  
  if (userMessages <= 1) return 1;  // Project Definitie
  if (userMessages <= 2) return 2;  // Doel & Succes
  if (userMessages <= 3) return 3;  // Team & Rollen
  if (userMessages <= 4) return 4;  // Scope & Grenzen
  if (userMessages <= 5) return 5;  // Deliverables
  if (userMessages <= 6) return 6;  // Tijdlijn
  return 7;                          // Resources
}

/**
 * Get the next step number based on conversation progress
 * For 7 steps: roughly 1-2 user messages per step
 */
export function calculateCurrentStep(history: SparringMessage[]): number {
  // ✅ FIX: Only count non-empty user messages
  const userMessages = history.filter(m => m.role === 'user' && m.content.trim().length > 0).length;
  
  // 7 steps, roughly 1-2 exchanges per step
  if (userMessages <= 1) return 1;  // Einddoel
  if (userMessages <= 2) return 2;  // Waarom
  if (userMessages <= 3) return 3;  // 1 Jaar
  if (userMessages <= 4) return 4;  // 6 Maanden
  if (userMessages <= 5) return 5;  // 3 Maanden
  if (userMessages <= 6) return 6;  // Deze Week
  return 7;                          // Actieplan
}

// -----------------------------------------------------------------------------
// VOORBEELDEN (for reference/testing)
// -----------------------------------------------------------------------------

/*
GOEDE VRAGEN PER STAP:

Stap 1 (Einddoel):
✅ "Wat is je ultieme doel? Maak het zo concreet mogelijk."
✅ "Welk getal of resultaat wil je uiteindelijk bereiken?"

Stap 2 (Waarom):
✅ "Waarom is dit doel zo belangrijk voor je?"
✅ "Wat zou het voor je betekenen als je dit bereikt?"

Stap 3 (1 Jaar):
✅ "Waar wil je over precies 1 jaar staan?"
✅ "Welk meetbaar resultaat wil je over 1 jaar zien?"

Stap 4 (6 Maanden):
✅ "Wat moet er over 6 maanden bereikt zijn?"
✅ "Welke strategie zou je kunnen gebruiken?"

Stap 5 (3 Maanden):
✅ "Wat is je focus voor de komende 3 maanden?"
✅ "Wat is het fundament dat je moet bouwen?"

Stap 6 (Deze Week):
✅ "Wat is de eerste concrete actie die je DEZE WEEK doet?"
✅ "Wie ga je benaderen? Wat ga je maken?"

Stap 7 (Actieplan):
✅ Samenvatting maken van alle genoemde mijlpalen
✅ Afsluiten met uitnodiging om vast te leggen

ANTI-PATTERNS - AI mag NOOIT:
❌ Zelf getallen of mijlpalen voorstellen
❌ Zelf strategieën aanbevelen
❌ Oordelen over haalbaarheid
❌ Meer dan 2-3 zinnen per reactie
*/
